<script lang="ts" module>
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import type { ClassValue, HTMLInputAttributes } from "svelte/elements";
	import check from "@core/assets/icons/check.svg?raw";
	import errorIcon from "@core/assets/icons/error.svg?raw";
	import { Locale, type LocalizedString } from "@core/schema/Locale.js";
	import { emailPattern } from "@core/utils/email.js";
	import { entries } from "@core/utils/entries.js";
	import { sameValueForEveryLocale } from "@core/utils/sameValueForEveryLocale.js";
	import { tw } from "@core/utils/svelte/tw.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";

	const customValidationMessage: Record<NonNullable<HTMLInputAttributes["type"]>, LocalizedString> = {
		email: {
			[Locale.cs]: "Zadejte platnou e-mailovou adresu",
			[Locale.en]: "Enter a valid email address",
		},
		url: {
			[Locale.cs]: "URL adresa (včetně https://)",
			[Locale.en]: "URL address (including https://)",
		},
	};

	const customValidationMessages: Record<keyof ValidityState, (input: HTMLInputElement) => LocalizedString> = {
		valid: () => sameValueForEveryLocale(""),
		customError: ({ validationMessage }) => sameValueForEveryLocale(validationMessage),
		valueMissing: () => ({
			[Locale.cs]: "Toto pole je povinné",
			[Locale.en]: "This field is required",
		}),
		typeMismatch: ({ type }) =>
			customValidationMessage[type] ?? {
				[Locale.cs]: "Zadejte platnou hodnotu",
				[Locale.en]: "Enter a valid value",
			},
		patternMismatch: () => ({
			[Locale.cs]: "Zadejte hodnotu ve správném formátu",
			[Locale.en]: "Enter a value in the correct format",
		}),
		badInput: () => ({
			[Locale.cs]: "Zadejte platnou hodnotu",
			[Locale.en]: "Enter a valid value",
		}),
		rangeOverflow: (input) => ({
			[Locale.cs]: `Zadejte hodnotu menší nebo rovnou ${input.max}`,
			[Locale.en]: `Enter a value less than or equal to ${input.max}`,
		}),
		rangeUnderflow: (input) => ({
			[Locale.cs]: `Zadejte hodnotu větší nebo rovnou ${input.min}`,
			[Locale.en]: `Enter a value greater than or equal to ${input.min}`,
		}),
		stepMismatch: (input) => ({
			[Locale.cs]: `Zadejte hodnotu dělitelnou ${input.step}`,
			[Locale.en]: `Enter a value that matches the specified step ${input.step}`,
		}),
		tooLong: (input) => ({
			[Locale.cs]: `Zadejte nejvýše ${input.maxLength} znaků`,
			[Locale.en]: `Enter no more than ${input.maxLength} characters`,
		}),
		tooShort: (input) => ({
			[Locale.cs]: `Zadejte nejméně ${input.minLength} znaků`,
			[Locale.en]: `Enter at least ${input.minLength} characters`,
		}),
	};
</script>

<script lang="ts">
	let {
		value = $bindable(),
		group = $bindable(),
		checked = $bindable(),
		input = $bindable(),
		/* eslint-disable prefer-const */
		placeholder,
		wrapperClass,
		...rest
		/* eslint-enable prefer-const */
	}: Omit<HTMLInputAttributes, "value" | "group"> & {
		wrapperClass?: ClassValue;
		group?: unknown;
		value?: string;
		input?: HTMLInputElement;
	} = $props();
	const { tenant } = getGlobalTemplateProps();
	const componentId = $props.id();

	const type = $derived(rest.type ?? "text");
	const customPattern: Partial<Record<NonNullable<HTMLInputAttributes["type"]>, string>> = {
		email: emailPattern,
		phone: "\\+?[0-9\\s-]{7,}",
	};
	const pattern = $derived(rest.pattern ?? customPattern[type]);
	const isRadioOrCheckbox = $derived(["checkbox", "radio"].includes(String(rest.type)));

	let validityMessage = $state("");

	function validate({ currentTarget }: Event & { currentTarget: EventTarget & HTMLInputElement }): void {
		const [, message] = entries(customValidationMessages).find(([key]) => currentTarget.validity[key]) ?? [];
		validityMessage = message?.(currentTarget)[tenant.locale] ?? "";
	}

	const inputProps = $derived<HTMLInputAttributes>({
		...rest,
		// For acessibility, always provide with the real placeholder, and as it acts as a label as well, label the input by it.
		"aria-placeholder": placeholder,
		"aria-labelledby": isRadioOrCheckbox ? undefined : componentId,
		class: tw(
			"min-w-0 block shadow-sm transition-[outline,color] duration-300 !outline-none font-medium",
			isRadioOrCheckbox
				? [
						"appearance-none size-6 border checked:border-fcrdarkgray disabled:cursor-not-allowed",
						"cursor-pointer checked:border-fcrdarkgray checked:bg-fcrdarkgray",
						type === "radio" ? "rounded-full border-fcrgray" : "rounded-lg border-fcrlightgray",
					]
				: [
						"min-h-16 md:min-h-18 border border-fcrlightgray-300 rounded-3xl bg-fcrlightgray-200",
						"text-base text-fcrdarkgray placeholder:text-fcrgray px-6 peer focus:border-fcrgray focus:border-2",
						// If the value is not empty, change the padding so the label can be there.
						value && "pt-7 pb-1",
						// If there is a label, and the input is focused, even when the value is empty,
						// change the padding so the label can be there
						"[&[aria-placeholder]:focus]:pt-7 [&[aria-placeholder]:focus]:pb-1",
						"transition-[padding] duration-200 w-full disabled:text-fcrgray",
						// Invalid input styles
						validityMessage && "[&:user-invalid]:border-fcrerror [&:user-invalid]:border-2",
					],
			type === "search" && "pr-[2rem]",
			rest.class,
		),
		pattern,
		oninput: (event: Event & { currentTarget: EventTarget & HTMLInputElement }): void => {
			rest.oninput?.(event);
			validate(event);
		},
		oninvalid: (event: Event & { currentTarget: EventTarget & HTMLInputElement }): void => {
			if (!isRadioOrCheckbox) {
				event.preventDefault();
				validate(event);
			}
			rest.oninvalid?.(event);
		},
	});
</script>

<div class={["relative", wrapperClass]}>
	{#if type === "checkbox" || type === "radio"}
		{#if type === "radio" || group}
			<input {...inputProps} bind:group {value} bind:this={input} />
		{:else}
			<input {...inputProps} type="checkbox" bind:checked {value} bind:this={input} />
		{/if}
		<div class="absolute inset-0 flex justify-center items-center [:not(:checked)+&]:hidden pointer-events-none">
			{#if rest.type === "checkbox"}
				<Icon icon={check} class="size-4 text-white" />
			{:else}
				<div class="size-2 bg-white rounded-full"></div>
			{/if}
		</div>
	{:else}
		<div class="relative">
			<input {...inputProps} bind:value bind:this={input} />
			{#if placeholder}
				<div
					class={[
						"absolute inset-0 text-fcrgray flex px-6 items-center font-medium duration-200",
						"transition-all pointer-events-none size-full",
						"peer-focus:text-xs/6 peer-focus:-translate-y-2",
						value ? "text-xs/6 -translate-y-2" : "translate-x-px",
					]}
					id={componentId}
				>
					<span class="h-6 line-clamp-1">
						<Formatted text={placeholder} />
					</span>
				</div>
			{/if}
			<div class={["absolute inset-0 hidden peer-[:user-invalid]:flex items-center justify-end pointer-events-none"]}>
				<div class="pr-6">
					<Icon icon={errorIcon} class="size-5.5 text-fcrerror" />
				</div>
			</div>
		</div>
		<div class="justify-end text-sm text-fcrerror pr-6 pt-1 hidden [:has(:user-invalid)+&:not(:empty)]:flex">
			<Formatted text={validityMessage} />
		</div>
	{/if}
</div>
